import {useState, useRef} from 'react';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Footer} from '@common/ui/footer/footer';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@common/i18n/trans';
import {LocalCafeIcon} from '@common/icons/material/LocalCafe';
import {FavoriteIcon} from '@common/icons/material/Favorite';
import {ContentCopyIcon} from '@common/icons/material/ContentCopy';
import {CheckIcon} from '@common/icons/material/Check';
import {EditIcon} from '@common/icons/material/Edit';
import {QrCodeIcon} from '@common/icons/material/QrCode';
import {ShieldIcon} from '@common/icons/material/Shield';
import {toast} from '@common/ui/toast/toast';
import clsx from 'clsx';

function readGroup3Digits(group: number, showZeroHundred: boolean): string {
  const digits = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
  const h = Math.floor(group / 100);
  const t = Math.floor((group % 100) / 10);
  const u = group % 10;

  let res = '';
  if (h > 0 || showZeroHundred) {
    res += digits[h] + ' trăm ';
  }

  if (t > 1) {
    res += digits[t] + ' mươi ';
    if (u === 1) res += 'mốt ';
    else if (u === 4) res += 'tư ';
    else if (u === 5) res += 'lăm ';
    else if (u > 0) res += digits[u] + ' ';
  } else if (t === 1) {
    res += 'mười ';
    if (u === 1) res += 'một ';
    else if (u === 4) res += 'bốn ';
    else if (u === 5) res += 'lăm ';
    else if (u > 0) res += digits[u] + ' ';
  } else if (t === 0) {
    if ((h > 0 || showZeroHundred) && u > 0) {
      res += 'lẻ ' + digits[u] + ' ';
    } else if (u > 0) {
      res += digits[u] + ' ';
    }
  }
  return res.trim();
}

function numberToVietnameseWords(n: number): string {
  if (!n || n <= 0 || isNaN(n)) return '';

  let numStr = Math.floor(n).toString();
  if (numStr === '0') return 'Không đồng';

  const groups: number[] = [];
  while (numStr.length > 0) {
    groups.unshift(parseInt(numStr.slice(-3), 10));
    numStr = numStr.slice(0, -3);
  }

  const scaleUnits = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ', 'tỷ tỷ'];
  const words: string[] = [];
  const totalGroups = groups.length;

  for (let i = 0; i < totalGroups; i++) {
    const group = groups[i];
    if (group === 0) continue;

    const showZeroHundred = i > 0;
    const groupText = readGroup3Digits(group, showZeroHundred);

    const scaleIdx = totalGroups - 1 - i;
    const unit = scaleUnits[scaleIdx] || '';

    words.push(groupText + (unit ? ' ' + unit : ''));
  }

  let result = words.join(' ').trim();
  if (!result) return '';

  result = result.charAt(0).toUpperCase() + result.slice(1) + ' đồng';
  return result;
}

function formatAmountShort(amount: number): string {
  if (!amount || isNaN(amount)) return '0K';
  if (amount >= 1000000000) {
    const ty = Math.round((amount / 1000000000) * 100) / 100;
    return ty + ' Ty';
  } else if (amount >= 1000000) {
    const tr = Math.round((amount / 1000000) * 100) / 100;
    return tr + ' Tr';
  } else if (amount >= 1000) {
    return Math.round(amount / 1000) + 'K';
  } else {
    return amount + 'd';
  }
}

function formatVND(num: number): string {
  if (!num || isNaN(num)) return '0 VNĐ';
  return new Intl.NumberFormat('vi-VN').format(num) + ' VNĐ';
}

function removeVietnameseTones(str: string): string {
  if (!str) return '';
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
  str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
  str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
  str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
  str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
  str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
  str = str.replace(/Đ/g, 'D');
  return str;
}

function parseCurrency(str: string): number {
  if (!str) return 0;
  let digits = str.toString().replace(/\D/g, '');
  if (digits.length > 15) digits = digits.slice(0, 15);
  return digits ? parseInt(digits, 10) : 0;
}

function formatNumber(num: number): string {
  if (!num || isNaN(num)) return '';
  return new Intl.NumberFormat('vi-VN').format(num);
}

const TIERS = [
  {id: '20000', amount: 20000, icon: '☕', label: '1 Tách', sub: '20.000đ'},
  {id: '50000', amount: 50000, icon: '☕☕', label: '2 Tách', sub: '50.000đ'},
  {id: '100000', amount: 100000, icon: '☕☕☕', label: '3 Tách', sub: '100.000đ'},
  {id: 'custom', amount: 200000, icon: '🎁', label: 'Tùy chọn', sub: 'Khác'},
];

const BANK_INFO = {
  name: 'BVBank (Bản Việt)',
  accountNumber: '8017041108750',
  accountHolder: 'NGUYEN TAN LOI',
};

export function DonatePage() {
  const [selectedTier, setSelectedTier] = useState<string>('20000');
  const [customAmount, setCustomAmount] = useState<number>(200000);
  const [customAmountText, setCustomAmountText] = useState<string>('200.000');
  const [donorName, setDonorName] = useState<string>('');
  const [donorMessage, setDonorMessage] = useState<string>('');
  const [copiedAcc, setCopiedAcc] = useState<boolean>(false);
  const [copiedMemo, setCopiedMemo] = useState<boolean>(false);
  const [isInputHighlighted, setIsInputHighlighted] = useState<boolean>(false);

  const nameInputRef = useRef<HTMLInputElement>(null);

  const activeAmount = selectedTier === 'custom' ? customAmount : parseInt(selectedTier, 10);
  const amountShort = formatAmountShort(activeAmount);
  const amountInWords = numberToVietnameseWords(activeAmount);

  const cleanName = removeVietnameseTones(donorName.trim())
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .toUpperCase();

  const isUnlocked = cleanName.length >= 2;
  const memoText = isUnlocked
    ? `${cleanName} Donate TDMUDEV ${amountShort}`
    : `(Chờ quý danh) Donate TDMUDEV ${amountShort}`;

  const qrUrl = `https://img.vietqr.io/image/VCCB-8017041108750-qr_only.png?amount=${activeAmount}&addInfo=${encodeURIComponent(
    cleanName ? `${cleanName} Donate TDMUDEV ${amountShort}` : ''
  )}&accountName=${encodeURIComponent(BANK_INFO.accountHolder)}`;

  const handleSelectTier = (tierId: string) => {
    setSelectedTier(tierId);
    if (tierId === 'custom') {
      setTimeout(() => {
        const customInput = document.getElementById('custom-amount-field');
        customInput?.focus();
      }, 100);
    }
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = parseCurrency(e.target.value);
    setCustomAmount(rawVal);
    if (rawVal > 0) {
      setCustomAmountText(formatNumber(rawVal));
    } else {
      setCustomAmountText('');
    }
  };

  const handleFocusName = () => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
      setIsInputHighlighted(true);
      setTimeout(() => {
        setIsInputHighlighted(false);
      }, 1500);
    }
  };

  const copyAccountNumber = () => {
    navigator.clipboard.writeText(BANK_INFO.accountNumber);
    setCopiedAcc(true);
    toast('Đã sao chép số tài khoản BVBank!');
    setTimeout(() => setCopiedAcc(false), 2000);
  };

  const copyMemo = () => {
    if (isUnlocked) {
      navigator.clipboard.writeText(memoText);
      setCopiedMemo(true);
      toast('Đã sao chép nội dung chuyển khoản!');
      setTimeout(() => setCopiedMemo(false), 2000);
    }
  };

  return (
    <div className="flex flex-col bg-[#f3f2f1] dark:bg-[#1b1a19] min-h-screen text-[#323130] dark:text-[#f3f2f1]">
      <StaticPageTitle>
        <Trans message="Mời cà phê - TDMU.DEV" />
      </StaticPageTitle>
      <Navbar className="flex-shrink-0 sticky top-0 z-20 shadow-sm" menuPosition="donate-page" />

      <main className="container max-w-4xl mx-auto px-16 sm:px-20 py-18 md:py-24 flex-auto">
        {/* Hero Banner with exact QRCDR gradient and styling */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0078d4]/10 via-[#ffb900]/10 to-[#ffb900]/15 dark:from-[#0078d4]/20 dark:via-slate-800 dark:to-[#ffb900]/20 border border-[#0078d4]/15 dark:border-slate-700/60 p-20 md:p-28 text-center shadow-sm mb-20">
          <div className="relative z-10 max-w-lg mx-auto flex flex-col items-center">
            {/* Round coffee icon badge */}
            <div className="w-52 h-52 rounded-full bg-[#ffb900]/20 dark:bg-[#ffb900]/25 text-[#d97706] dark:text-[#fbbf24] flex items-center justify-center shadow-sm mb-12 border border-[#ffb900]/30">
              <LocalCafeIcon className="w-26 h-26" />
            </div>

            <h1 className="text-xl md:text-2xl font-extrabold text-[#201f1e] dark:text-white tracking-tight mb-8">
              Mời đội ngũ 1 Tách Cà Phê ☕
            </h1>

            <div className="text-[#605e5c] dark:text-slate-300 text-xs md:text-sm leading-relaxed mb-14 space-y-2">
              <p>
                <strong className="text-[#201f1e] dark:text-white font-semibold">TDMU.DEV - URL Shortener</strong>{' '}
                phục vụ miễn phí cho cộng đồng học tập và làm việc.
              </p>
              <p>
                Sự đóng góp của bạn giúp chúng tôi tiếp tục duy trì máy chủ và nâng cấp các tính năng mới!
              </p>
            </div>

            <div className="inline-flex items-center gap-6 px-14 py-6 rounded-full bg-white/90 dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 text-[#605e5c] dark:text-slate-300 text-xs font-medium shadow-sm">
              <FavoriteIcon className="w-14 h-14 text-red-500" />
              <span>Cảm ơn bạn đã luôn đồng hành & ủng hộ dự án!</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-18">
          {/* Column Left: Coffee Selector & Supporter Note */}
          <div className="lg:col-span-7 space-y-18">
            {/* Step 1: Select Coffee Tiers */}
            <div className="rounded-2xl border border-slate-200/90 dark:border-slate-700/60 bg-white dark:bg-slate-800 p-16 sm:p-20 shadow-sm">
              <div className="flex items-center gap-8 mb-16">
                <span className="flex items-center justify-center w-24 h-24 rounded-full bg-[#0078d4] text-white font-bold text-xs shadow-sm">
                  1
                </span>
                <h2 className="text-sm sm:text-base font-bold text-[#201f1e] dark:text-white">
                  Chọn mức ủng hộ Cà phê
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-10">
                {TIERS.map(tier => {
                  const isActive = selectedTier === tier.id;
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      onClick={() => handleSelectTier(tier.id)}
                      className={clsx(
                        'flex flex-col items-center justify-center p-12 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer select-none',
                        isActive
                          ? 'border-[#0078d4] bg-[#0078d4]/10 dark:bg-[#0078d4]/25 text-[#0078d4] dark:text-[#38daf6] shadow-sm transform -translate-y-1'
                          : 'border-[#0078d4] bg-white dark:bg-slate-800/80 text-[#0078d4] dark:text-[#38daf6] hover:bg-[#0078d4]/5 dark:hover:bg-[#0078d4]/15'
                      )}
                    >
                      <div className="text-xl mb-3">{tier.icon}</div>
                      <div className="text-xs sm:text-sm font-bold">
                        {tier.label}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 font-medium opacity-85">
                        {tier.sub}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Custom Amount Input */}
              {selectedTier === 'custom' && (
                <div className="mt-16 p-14 rounded-xl bg-[#f8fafc] dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700">
                  <label
                    htmlFor="custom-amount-field"
                    className="block text-xs font-semibold text-[#605e5c] dark:text-slate-300 mb-6"
                  >
                    Số tiền tùy chọn (VNĐ)
                  </label>
                  <div className="relative flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus-within:border-[#0078d4] focus-within:ring-2 focus-within:ring-[#0078d4]/20 overflow-hidden shadow-inner">
                    <input
                      id="custom-amount-field"
                      type="text"
                      inputMode="numeric"
                      value={customAmountText}
                      onChange={handleCustomAmountChange}
                      placeholder="Ví dụ: 200.000"
                      className="w-full px-12 py-8 bg-transparent text-base font-bold text-[#0078d4] dark:text-[#38daf6] tracking-wide outline-none"
                    />
                    <span className="px-12 py-8 bg-[#e6f2fb] dark:bg-[#0078d4]/20 border-l border-slate-200 dark:border-slate-700 font-bold text-[#0078d4] dark:text-[#38daf6] text-xs sm:text-sm">
                      VNĐ
                    </span>
                  </div>
                  {amountInWords && (
                    <div className="mt-8 flex items-center gap-6 text-xs text-[#0078d4] dark:text-[#38daf6] font-medium flex-wrap">
                      <span className="px-8 py-2 rounded bg-[#e6f2fb] dark:bg-[#0078d4]/20 font-semibold">
                        Bằng chữ:
                      </span>
                      <span className="font-bold">{amountInWords}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Supporter Info */}
            <div className="rounded-2xl border border-slate-200/90 dark:border-slate-700/60 bg-white dark:bg-slate-800 p-16 sm:p-20 shadow-sm">
              <div className="flex items-center gap-8 mb-16">
                <span className="flex items-center justify-center w-24 h-24 rounded-full bg-[#0078d4] text-white font-bold text-xs shadow-sm">
                  2
                </span>
                <h2 className="text-sm sm:text-base font-bold text-[#201f1e] dark:text-white">
                  Thông tin & Lời nhắn gửi
                </h2>
              </div>

              <div className="space-y-14">
                <div>
                  <label
                    htmlFor="donor-name-input"
                    className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-6"
                  >
                    Quý danh của bạn (Họ & tên / Biệt danh) <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={nameInputRef}
                    id="donor-name-input"
                    type="text"
                    value={donorName}
                    onChange={e => setDonorName(e.target.value)}
                    placeholder="Xin quý danh của nhà hảo tâm..."
                    autoComplete="name"
                    className={clsx(
                      'w-full px-12 py-8 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none transition-all duration-200 shadow-sm text-sm',
                      isInputHighlighted
                        ? 'border-[#0078d4] ring-4 ring-[#0078d4]/30 scale-[1.01]'
                        : 'border-slate-300 dark:border-slate-600 focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/20'
                    )}
                  />
                  <p className="mt-6 text-xs text-slate-500 dark:text-slate-400 leading-relaxed flex items-center gap-4">
                    <span>📄</span>
                    <span>Cho chúng mình xin quý danh để tiện bề &quot;sao kê&quot;, tri ân công khai & minh bạch nhé! 😄☕</span>
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="donor-message-input"
                    className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-6"
                  >
                    Lời nhắn gửi (Tùy chọn)
                  </label>
                  <textarea
                    id="donor-message-input"
                    rows={3}
                    value={donorMessage}
                    onChange={e => setDonorMessage(e.target.value)}
                    placeholder="Gửi vài lời tâm sự hoặc động viên tới đội ngũ phát triển..."
                    className="w-full px-12 py-8 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/20 transition-all shadow-sm text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Column Right: QR & Transfer Card */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-slate-200/90 dark:border-slate-700/60 bg-white dark:bg-slate-800 p-16 sm:p-20 shadow-sm sticky top-80 space-y-14">
              <h3 className="text-sm sm:text-base font-bold text-center text-[#201f1e] dark:text-white">
                Chuyển khoản ủng hộ
              </h3>

              {/* QR Code Container */}
              <div className="p-12 rounded-xl bg-[#f8fafc] dark:bg-slate-900/80 border border-slate-200 dark:border-slate-700 max-w-[300px] mx-auto shadow-sm">
                {!isUnlocked ? (
                  /* Placeholder state when name is not entered */
                  <div className="p-16 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 min-h-[250px]">
                    <div className="w-50 h-50 rounded-full bg-[#ffb900]/20 dark:bg-[#ffb900]/25 text-[#d97706] dark:text-[#fbbf24] flex items-center justify-center mb-10 shadow-sm border border-[#ffb900]/30">
                      <LocalCafeIcon className="w-24 h-24" />
                    </div>
                    <h4 className="font-bold text-[#201f1e] dark:text-white text-xs sm:text-sm mb-4">
                      Xin quý danh để mở mã QR ☕
                    </h4>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-14 leading-relaxed max-w-[220px]">
                      Vui lòng điền <strong>Quý danh</strong> ở bước 2 để chúng mình &quot;sao kê&quot; minh bạch và mở khóa mã QR nhé!
                    </p>
                    <button
                      type="button"
                      onClick={handleFocusName}
                      className="inline-flex items-center gap-6 px-14 py-5 rounded-full border border-[#0078d4] text-[#0078d4] dark:text-[#38daf6] dark:border-[#38daf6] bg-white hover:bg-[#0078d4]/10 dark:bg-slate-800 dark:hover:bg-[#0078d4]/20 text-xs font-semibold shadow-sm transition-all active:scale-95 cursor-pointer"
                    >
                      <EditIcon className="w-13 h-13" />
                      <span>Điền quý danh ngay</span>
                    </button>
                  </div>
                ) : (
                  /* Unlocked QR Code */
                  <div className="text-center space-y-8 animate-fade-in">
                    <div className="p-6 rounded-lg bg-white shadow-sm inline-block w-full border border-slate-200">
                      <img
                        src={qrUrl}
                        alt="Mã QR VietQR"
                        className="w-full h-auto max-h-[280px] object-contain rounded-md mx-auto"
                      />
                    </div>
                    <div className="flex items-center justify-center gap-6 py-6 px-10 rounded-lg bg-[#e6f2fb] dark:bg-[#0078d4]/25 text-[#0078d4] dark:text-[#38daf6] text-xs font-semibold">
                      <QrCodeIcon className="w-15 h-15" />
                      <span>Quét mã VietQR Chuyển khoản</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Transfer Details */}
              <div className="rounded-xl bg-[#f8fafc] dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/70 p-12 text-xs space-y-8">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">Ngân hàng:</span>
                  <span className="font-bold text-[#0078d4] dark:text-[#38daf6]">{BANK_INFO.name}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">Số tài khoản:</span>
                  <div className="flex items-center gap-6">
                    <span className="font-mono font-bold text-slate-900 dark:text-white text-sm">
                      {BANK_INFO.accountNumber}
                    </span>
                    <button
                      type="button"
                      onClick={copyAccountNumber}
                      className="p-3 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-[#0078d4] dark:text-[#38daf6] transition-colors cursor-pointer"
                      title="Sao chép STK"
                    >
                      {copiedAcc ? (
                        <CheckIcon className="w-15 h-15 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <ContentCopyIcon className="w-15 h-15" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">Chủ tài khoản:</span>
                  <span className="font-bold text-slate-900 dark:text-white">{BANK_INFO.accountHolder}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">Số tiền ủng hộ:</span>
                  <span className="font-bold text-[#107c41] dark:text-emerald-400 text-sm">
                    {formatVND(activeAmount)}
                  </span>
                </div>

                <div className="flex justify-between items-start">
                  <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">Bằng chữ:</span>
                  <span className="font-bold text-[#0078d4] dark:text-[#38daf6] text-right pl-8">
                    {amountInWords || 'Không đồng'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">Nội dung CK:</span>
                  <div className="flex items-center gap-6">
                    <span
                      className={clsx(
                        'font-mono text-right',
                        isUnlocked
                          ? 'font-bold text-red-600 dark:text-red-400'
                          : 'italic text-slate-400 dark:text-slate-500'
                      )}
                    >
                      {memoText}
                    </span>
                    {isUnlocked && (
                      <button
                        type="button"
                        onClick={copyMemo}
                        className="p-3 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-[#0078d4] dark:text-[#38daf6] transition-colors cursor-pointer"
                        title="Sao chép nội dung"
                      >
                        {copiedMemo ? (
                          <CheckIcon className="w-15 h-15 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <ContentCopyIcon className="w-15 h-15" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Alert Info */}
              <div className="flex items-start gap-8 p-10 rounded-xl bg-[#ecfdf5] dark:bg-emerald-950/30 border border-[#a7f3d0] dark:border-emerald-800 text-[#065f46] dark:text-emerald-300 text-[11px]">
                <ShieldIcon className="w-16 h-16 flex-shrink-0 mt-2 text-[#059669] dark:text-emerald-400" />
                <span>
                  Mở ứng dụng Ngân hàng (App Mobile Banking) quét mã QR ở trên để chuyển khoản tự động.
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer className="w-full flex-shrink-0" padding="py-14 px-20 md:px-40" />
    </div>
  );
}
